/**
 * The favorite teams manager (ISSUE-1399): the fan's followed teams with a
 * Remove on each, and a way to add more. The web twin of v1's Settings >
 * Favorites.
 *
 * Adding has two modes. Typed text (two letters or more, after a short pause)
 * searches every league at once through sports-api's POST
 * /search/teams-players, the same read the Scores search uses; the server
 * matches and orders, and each row names its league. With the field empty or
 * one letter long, the fan browses one league's teams from the league picker.
 */
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import {
  useLeagueCatalogGet,
  useTeamGetMany,
  useSearchTeamsPlayers,
  useTeamList,
} from "@sportsmet/sports-sdk/private";
import {
  Button,
  cn,
  InlineBanner,
  Input,
  NEUTRAL_SURFACE_FRAME,
  Select,
  Skeleton,
} from "@sportsmet/ui";
import { keepPreviousData } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import {
  ADD_TEAMS_TITLE,
  buildTeamLeagueOptions,
  filterTeams,
  FOLLOW_LABEL,
  FOLLOWING_LABEL,
  LEAGUE_PICKER_LABEL,
  LEAGUE_PICKER_PLACEHOLDER,
  leagueShortNamesById,
  NO_TEAM_MATCH,
  PICK_LEAGUE_FIRST,
  REMOVE_LABEL,
  SEARCH_ANY_LEAGUE_FAILED,
  searchableTeamQuery,
  TEAM_SEARCH_DEBOUNCE_MS,
  TEAM_SEARCH_LABEL,
  TEAM_SEARCH_PLACEHOLDER,
  TEAMS_LOAD_FAILED,
  TEAMS_LOADING_LABEL,
  YOUR_TEAMS_EMPTY,
  YOUR_TEAMS_TITLE,
} from "./favoriteTeamsModel";
import { useFavoriteTeamWrite } from "./useFavoriteTeamWrite";

const SECTION_CLASS =
  "flex flex-col gap-3 rounded-lg border border-line bg-surface p-4";

const TeamMark = ({ abbreviation }: { abbreviation: string }): ReactNode => (
  <span
    aria-hidden="true"
    className={cn(
      "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-elevated text-small text-ink",
      NEUTRAL_SURFACE_FRAME,
    )}
  >
    {abbreviation}
  </span>
);

const TeamRow = ({
  abbreviation,
  name,
  subtitle,
  action,
  testId,
}: {
  abbreviation: string;
  name: string;
  subtitle?: string | undefined;
  action: ReactNode;
  testId: string;
}): ReactNode => (
  <li className="flex items-center gap-3" data-testid={testId}>
    <TeamMark abbreviation={abbreviation} />
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="truncate text-body-strong text-ink">{name}</span>
      {subtitle === undefined ? null : (
        <span className="text-caption text-ink-secondary">{subtitle}</span>
      )}
    </div>
    {action}
  </li>
);

export const FavoriteTeamsPane = (): ReactNode => {
  const profileQuery = useProfileGet({});
  const catalog = useLeagueCatalogGet({});
  const write = useFavoriteTeamWrite();

  const favoriteTeamIds =
    profileQuery.data?.data?.profile?.favoriteTeamIds ?? [];
  const followedLeagueIds =
    profileQuery.data?.data?.profile?.followedLeagueIds ?? [];
  const sports = catalog.data?.data?.sports ?? [];

  const favorites = useTeamGetMany(
    { teamIds: favoriteTeamIds },
    { enabled: favoriteTeamIds.length > 0 },
  );
  const leagueNames = useMemo(() => leagueShortNamesById(sports), [sports]);
  const leagueOptions = useMemo(
    () => buildTeamLeagueOptions({ sports, followedLeagueIds }),
    [sports, followedLeagueIds],
  );

  const [leagueId, setLeagueId] = useState<string | undefined>(undefined);
  const [query, setQuery] = useState("");
  const [settledQuery, setSettledQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setSettledQuery(query);
    }, TEAM_SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [query]);
  const searchQuery = searchableTeamQuery(settledQuery);
  const isSearching = searchableTeamQuery(query) !== null;
  const search = useSearchTeamsPlayers(
    { query: searchQuery ?? "" },
    { enabled: searchQuery !== null, placeholderData: keepPreviousData },
  );

  const pickedLeagueId = leagueId ?? leagueOptions[0]?.value;
  const leagueTeams = useTeamList(
    { leagueId: pickedLeagueId ?? "" },
    { enabled: pickedLeagueId !== undefined && !isSearching },
  );
  const matches = filterTeams(
    (leagueTeams.data?.data?.teams ?? []).filter((team) => team.isActive),
    query,
  );

  const favoriteRows = favorites.data?.data?.teams ?? [];
  const favoriteSet = new Set(favoriteTeamIds);

  const followButton = (team: { teamId: string; name: string }): ReactNode => {
    const isFollowing = favoriteSet.has(team.teamId);
    return (
      <Button
        variant={isFollowing ? "secondary" : "outline"}
        size="sm"
        aria-pressed={isFollowing}
        aria-label={`${isFollowing ? FOLLOWING_LABEL : FOLLOW_LABEL} ${team.name}`}
        loading={write.pendingTeamId === team.teamId}
        loadingLabel={isFollowing ? FOLLOWING_LABEL : FOLLOW_LABEL}
        onClick={() => {
          void write.setFollowing(team.teamId, !isFollowing);
        }}
        data-testid={`team-result-follow-${team.teamId}`}
      >
        {isFollowing ? FOLLOWING_LABEL : FOLLOW_LABEL}
      </Button>
    );
  };

  return (
    <div className="flex flex-col gap-4" data-testid="favorite-teams-pane">
      <section className={SECTION_CLASS} data-testid="favorite-teams-yours">
        <h2 className="text-h2 text-ink">{YOUR_TEAMS_TITLE}</h2>
        {profileQuery.isPending ||
        (favoriteTeamIds.length > 0 && favorites.isPending) ? (
          <Skeleton shape="text" label={TEAMS_LOADING_LABEL} />
        ) : favoriteTeamIds.length === 0 ? (
          <p
            className="text-body text-ink-secondary"
            data-testid="favorite-teams-empty"
          >
            {YOUR_TEAMS_EMPTY}
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {favoriteRows.map((team) => (
              <TeamRow
                key={team.teamId}
                testId={`favorite-team-${team.teamId}`}
                abbreviation={team.abbreviation}
                name={team.name}
                subtitle={leagueNames.get(team.leagueId)}
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={write.pendingTeamId === team.teamId}
                    loadingLabel={REMOVE_LABEL}
                    onClick={() => {
                      void write.setFollowing(team.teamId, false);
                    }}
                    aria-label={`${REMOVE_LABEL} ${team.name}`}
                    data-testid={`favorite-team-remove-${team.teamId}`}
                  >
                    {REMOVE_LABEL}
                  </Button>
                }
              />
            ))}
          </ul>
        )}
      </section>

      <section className={SECTION_CLASS} data-testid="favorite-teams-add">
        <h2 className="text-h2 text-ink">{ADD_TEAMS_TITLE}</h2>
        <Input
          label={TEAM_SEARCH_LABEL}
          placeholder={TEAM_SEARCH_PLACEHOLDER}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          type="search"
          autoComplete="off"
          data-testid="favorite-teams-search"
        />
        {isSearching ? (
          searchQuery === null ||
          (search.data?.data == null && search.isPending) ? (
            <Skeleton shape="text" label={TEAMS_LOADING_LABEL} />
          ) : search.isError || search.data?.error != null ? (
            <InlineBanner
              variant="warning"
              message={SEARCH_ANY_LEAGUE_FAILED}
              action={{
                label: "Try again",
                onAction: () => {
                  void search.refetch();
                },
              }}
            />
          ) : (search.data?.data?.teams ?? []).length === 0 ? (
            <p
              className="text-body text-ink-secondary"
              data-testid="favorite-teams-no-match"
            >
              {NO_TEAM_MATCH}
            </p>
          ) : (
            <ul
              className="flex flex-col gap-3"
              data-testid="favorite-teams-search-results"
            >
              {(search.data?.data?.teams ?? []).map((team) => (
                <TeamRow
                  key={team.teamId}
                  testId={`team-result-${team.teamId}`}
                  abbreviation={team.abbreviation}
                  name={team.name}
                  subtitle={team.leagueName}
                  action={followButton(team)}
                />
              ))}
            </ul>
          )
        ) : (
          <>
            <Select
              label={LEAGUE_PICKER_LABEL}
              placeholder={LEAGUE_PICKER_PLACEHOLDER}
              items={leagueOptions}
              value={pickedLeagueId}
              onValueChange={(next) => {
                setLeagueId(next);
              }}
            />
            {pickedLeagueId === undefined ? (
              <p className="text-body text-ink-secondary">
                {PICK_LEAGUE_FIRST}
              </p>
            ) : leagueTeams.isPending ? (
              <Skeleton shape="text" label={TEAMS_LOADING_LABEL} />
            ) : leagueTeams.isError || leagueTeams.data?.error != null ? (
              <InlineBanner
                variant="warning"
                message={TEAMS_LOAD_FAILED}
                action={{
                  label: "Try again",
                  onAction: () => {
                    void leagueTeams.refetch();
                  },
                }}
              />
            ) : matches.length === 0 ? (
              <p
                className="text-body text-ink-secondary"
                data-testid="favorite-teams-no-match"
              >
                {NO_TEAM_MATCH}
              </p>
            ) : (
              <ul
                className="flex flex-col gap-3"
                data-testid="favorite-teams-results"
              >
                {matches.map((team) => (
                  <TeamRow
                    key={team.teamId}
                    testId={`team-result-${team.teamId}`}
                    abbreviation={team.abbreviation}
                    name={team.name}
                    action={followButton(team)}
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </section>
    </div>
  );
};
